import type {
  CreativeLabelOverride,
  InsufficientCreditsDetails,
  LabelEditAnalysisCost,
  LabelEditCommitResult,
  LabelEditPreview,
  LabelEditValidationErrorDetail,
} from './labelEdit.types'

function record(value: unknown, name: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`)
  }
  return value as Record<string, unknown>
}

function numberValue(value: unknown, name: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TypeError(`${name} must be a finite number`)
  }
  return value
}

function booleanValue(value: unknown, name: string): boolean {
  if (typeof value !== 'boolean') throw new TypeError(`${name} must be a boolean`)
  return value
}

function stringValue(value: unknown, name: string): string {
  if (typeof value !== 'string') throw new TypeError(`${name} must be a string`)
  return value
}

function analysisCosts(value: unknown, name: string): LabelEditAnalysisCost[] {
  if (!Array.isArray(value)) throw new TypeError(`${name} must be an array`)
  return value.map((item, index) => {
    const row = record(item, `${name}[${index}]`)
    return {
      id: numberValue(row.id, `${name}[${index}].id`),
      cost: numberValue(row.cost, `${name}[${index}].cost`),
    }
  })
}

export function decodeLabelEditPreview(value: unknown): LabelEditPreview {
  const row = record(value, 'preview')
  return {
    analyses: analysisCosts(row.analyses, 'preview.analyses'),
    total_cost: numberValue(row.total_cost, 'preview.total_cost'),
    remaining: numberValue(row.remaining, 'preview.remaining'),
    affordable: booleanValue(row.affordable, 'preview.affordable'),
  }
}

export function decodeLabelEditCommitResult(value: unknown): LabelEditCommitResult {
  const row = record(value, 'commit')
  const scope = row.scope
  if (scope !== 'image' && scope !== 'video')
    throw new TypeError('commit.scope must be image or video')
  return {
    scope,
    batch_id: stringValue(row.batch_id, 'commit.batch_id'),
    edits_applied: numberValue(row.edits_applied, 'commit.edits_applied'),
    creatives: numberValue(row.creatives, 'commit.creatives'),
    analyses: analysisCosts(row.analyses, 'commit.analyses'),
    total_cost: numberValue(row.total_cost, 'commit.total_cost'),
  }
}

export function decodeLabelOverrides(value: unknown): CreativeLabelOverride[] {
  if (!Array.isArray(value)) throw new TypeError('overrides must be an array')
  return value.map((item, index) => {
    const row = record(item, `overrides[${index}]`)
    return {
      asset_id: stringValue(row.asset_id, `overrides[${index}].asset_id`),
      label_key: stringValue(row.label_key, `overrides[${index}].label_key`),
      previous_value: row.previous_value ?? null,
    }
  })
}

export function readInsufficientCreditsDetails(
  details: unknown,
): InsufficientCreditsDetails | null {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return null
  const row = details as Record<string, unknown>
  if (typeof row.remaining !== 'number' || typeof row.required !== 'number') return null
  return {
    remaining: row.remaining,
    required: row.required,
    analysesCount: typeof row.analyses === 'number' ? row.analyses : 0,
  }
}

export function readValidationMessages(details: unknown): string[] {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return []
  const errors = (details as Record<string, unknown>).errors
  if (!Array.isArray(errors)) return []
  const messages: LabelEditValidationErrorDetail['message'][] = []
  for (const item of errors) {
    if (
      item &&
      typeof item === 'object' &&
      typeof (item as { message?: unknown }).message === 'string'
    ) {
      messages.push((item as { message: string }).message)
    }
  }
  return messages
}
